import { toast } from 'react-toastify';

import { ALERT_TYPES } from 'app/modules/alerts/core/constant';

import { getResponseError } from 'app/helpers/errors';

import { API_URL } from 'app/constants/environments';

import { useAlertStore } from '../store';
import { AlertGroup, AlertType, AlertsResponse, SuppressionResponse } from './type';
import axios from 'axios';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const ALERTS_URL = `${API_URL}/api`;

export const getTotalUnreadAlert = async () => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get(
      `${ALERTS_URL}/orgs/${organizationId}/alerts/count?orgId=${organizationId}`,
    );
    const dataRes = response.data;

    const fetched = useAlertStore.getState().state.fetched;
    const initUnReadCount = useAlertStore.getState().state.initUnReadCount;

    if (!dataRes) {
      return;
    }

    const updatedAlertGroup: AlertGroup = Object.fromEntries(
      ALERT_TYPES.map((type) => [
        type,
        {
          total: dataRes.data.summary?.[AlertType[type]] || 0,
          items: [],
        },
      ]),
    ) as unknown as AlertGroup;

    useAlertStore.getState().setState({
      unReadCount: dataRes.data.count,
      initUnReadCount: fetched ? initUnReadCount : dataRes.data.count,
      alertGroup: updatedAlertGroup,
    });

    return dataRes;
  } catch (error) {
    console.error('Error fetching unread alerts count:', error);
    return null;
  }
};

export const getAlertGroups = async (
  page = 1,
  limit = 10,
  type: AlertType,
): Promise<AlertsResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get(`${ALERTS_URL}/orgs/${organizationId}/alerts`, {
      params: { orgId: organizationId, page, limit, type },
    });
    const dataRes = response.data.data;

    if (!dataRes) throw new Error('No data response');

    return dataRes;
  } catch (error) {
    console.error('Error fetching alert groups:', error);
    getResponseError(error);
    throw new Error('Error fetching alert groups');
  }
};

export const changeStatusAlert = async (id: number) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.patch(`${ALERTS_URL}/orgs/${organizationId}/alerts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error changing alert status:', error);
  }
};

export const acknowledgeAlert = async (id: number) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.patch(
      `${ALERTS_URL}/orgs/${organizationId}/alerts/${id}/acknowledge`,
    );
    return response.data;
  } catch (error) {
    console.error('Error acknowledging alert:', error);
    getResponseError(error);
  }
};

export const resolveAlert = async (id: number, resolutionNote?: string) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.patch(
      `${ALERTS_URL}/orgs/${organizationId}/alerts/${id}/resolve`,
      { resolutionNote },
    );
    return response.data;
  } catch (error) {
    console.error('Error resolving alert:', error);
    getResponseError(error);
  }
};

export const updateMarkAllAlert = async (orgId: number) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.patch(
      `${ALERTS_URL}/orgs/${organizationId}/alerts/read-all?orgId=${orgId}`,
    );
    if (response) {
      useAlertStore.getState().onMarkAllRead();
      useAlertStore.getState().setState({
        initUnReadCount: 0,
        unReadCount: 0,
        refreshTimestamp: +new Date(),
      });
      return response.data;
    }
  } catch (error) {
    console.error('Error marking all alerts as read:', error);
  }
};

export const getAlertSuppression = async (type: AlertType): Promise<SuppressionResponse[]> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get<{
      success: boolean;
      data: SuppressionResponse[];
      message: string;
    }>(`${ALERTS_URL}/orgs/${organizationId}/alerts/suppression`, {
      params: { orgId: organizationId, type },
    });

    if (!response.data.success)
      throw new Error(response.data.message || 'Error fetching alert suppression');

    return response.data.data;
  } catch (error) {
    console.error('Error fetching alert suppression:', error);
    getResponseError(error);
    throw new Error('Error fetching alert suppression');
  }
};

export const updateAlertSuppression = async (
  type: AlertType,
  body: { relation: string | number; active: boolean }[],
): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.patch<{ success: boolean; message: string }>(
      `${ALERTS_URL}/orgs/${organizationId}/alerts/suppression`,
      body,
      {
        params: {
          orgId: organizationId,
          type,
        },
      },
    );

    if (!response.data.success)
      throw new Error(response.data.message || 'Error updating alert suppression');

    toast.success('Alert suppression updated successfully (SIMS 2.0)');
  } catch (error) {
    console.error('Error updating alert suppression:', error);
    getResponseError(error);
    throw new Error('Error updating alert suppression');
  }
};

// SIMS 2.5's own suppression state, independent of the SIMS 2.0 pair above —
// same shape, `-2-5` endpoint, separate table on the backend.
export const getAlertSuppression25 = async (type: AlertType): Promise<SuppressionResponse[]> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get<{
      success: boolean;
      data: SuppressionResponse[];
      message: string;
    }>(`${ALERTS_URL}/orgs/${organizationId}/alerts/suppression-2-5`, {
      params: { orgId: organizationId, type },
    });

    if (!response.data.success)
      throw new Error(response.data.message || 'Error fetching alert suppression (SIMS 2.5)');

    return response.data.data;
  } catch (error) {
    console.error('Error fetching alert suppression (SIMS 2.5):', error);
    getResponseError(error);
    throw new Error('Error fetching alert suppression (SIMS 2.5)');
  }
};

export const updateAlertSuppression25 = async (
  type: AlertType,
  body: { relation: string | number; active: boolean }[],
): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.patch<{ success: boolean; message: string }>(
      `${ALERTS_URL}/orgs/${organizationId}/alerts/suppression-2-5`,
      body,
      {
        params: {
          orgId: organizationId,
          type,
        },
      },
    );

    if (!response.data.success)
      throw new Error(response.data.message || 'Error updating alert suppression (SIMS 2.5)');

    toast.success('Alert suppression updated successfully (SIMS 2.5)');
  } catch (error) {
    console.error('Error updating alert suppression (SIMS 2.5):', error);
    getResponseError(error);
    throw new Error('Error updating alert suppression (SIMS 2.5)');
  }
};
